import React, {useEffect, useRef, useState} from 'react';
import {t} from 'i18n';

import {LoadState, RunGroupID, RunStatus, SSEEventType, SSEStatus, TestStatus, TestStatusGetID} from 'enums';
import {IDevice, IRunGroup, IRunGroupAPI, ISSEEvent, ISSEEventError, ISSEEventTestState, ITest, ITestAPI} from 'interfaces';
// Not using index import for services to avoid circular dependency
import {axiosServer, parseAxiosError} from './API';
import {DevicesService} from './DevicesService';
import {PubSub} from './PubSub';
import {RunGroupsService} from './RunGroupsService';
import {TestsService} from './TestsService';

export interface ITestsModelState {
    // State management
    isWaitingOnReboot: boolean,
    loadState: LoadState,
    loadError?: string,
    runStatus: RunStatus,
    runError?: string,
    sseStatus: SSEStatus,
    // Data
    device?: IDevice,
    events: ISSEEvent[],
    runDate?: Date,
    runGroups: IRunGroup[],
}

class _TestsModelFactory {
    constructor() {}

    public create(deviceId: number) {
        return new TestsModel(deviceId);
    }
}

type IContext = {
    isDone?: boolean,
}

// Responsible for getting the state of the tests for a run, including merging current state with real time events.
export class TestsModel {
    // Public state - This is grouped under an object so that we can easily expose it to hooks.
    public state: ITestsModelState = {
        isWaitingOnReboot: false,
        loadState: LoadState.Initial,
        runStatus: RunStatus.Initial,
        sseStatus: SSEStatus.Initial,
        events: [], 
        runGroups: [],
    }

    // Internal state
    private allTestsById: {[testId: string]: ITest} = {}
    private isDestroyed = false;
    private pubSubState = new PubSub<void>();
    private queuedSSEEvents: ISSEEvent[] = [];
    private runGroupsAPI: IRunGroupAPI[] = [];
    // Map to look up which rungroup contains a test.
    private runGroupsIdsByTestId: {[testId: string]: RunGroupID} = {};
    private runId: string;
    private sse: EventSource;
    private sseContext: IContext;

    constructor(private deviceId: number) {}

    /*
    Our strategy is to load the current run state and also subscribe to the run's events stream. The server replays all events upon connection.
    Flow:
        Load data we need:
            Device - Needed to show the device info.
            TestNames - The db is the source of truth for test names and descriptions.
            RunGroups - The certification server is the source of truth for available run groups. However,
                we handle the run group names in the frontend.
            RunInfo - The db state of the run and tests.
        if we have a previous run:
            Apply the run's state to our tests and our state.
            if "inprogress":
                Subscribe to events
                    onconnect:
                        Wait 1 second in order to give server time to replay events. This is so we prevent flashing for any
                        control events, like when the server prompts the user to reboot their device.
                    onmessage:
                        if after events delay
                            Apply event to our state.
                        else
                            Push event to queue.
                After 1 second delay:
                    Apply queued events to our state.
    */
    public load(): void {
        const devicePromise = DevicesService.getDevice(this.deviceId);
        const testNamesPromise = TestsService.getTestsInfo();
        const runGroupsPromise = RunGroupsService.load();
        // We need the runId to load the run, so we have to fetch the device first.
        const runPromise = devicePromise.then((device) => {
            if (this.isDestroyed) return;
            // If the device has already been run, fetch the run info
            if (device.last_run_id) {
                return TestsService.getRun(device.last_run_id);
            } else {
                return undefined;
            }
        });

        Promise.all([devicePromise, testNamesPromise, runGroupsPromise, runPromise])
            .then(([device, testNames, runGroupsAPI, run]) => {
                if (this.isDestroyed) return;
                // Load success! We have our initial data, map it into the formats we need.
                this.runId = device.last_run_id;
                this.runGroupsAPI = runGroupsAPI;

                // Store all tests by id
                for (const testInfo of testNames) {
                    this.allTestsById[testInfo.enumKey] = {
                        id: testInfo.id,
                        enumKey: testInfo.enumKey,
                        name: testInfo.name,
                        description: testInfo.description,
                        groupName: testInfo.groupName,
                        result: '',
                        status: TestStatus.NotStarted,
                    }
                }

                // Create map to look up which run group is associated with a test.
                for (const runGroup of runGroupsAPI) {
                    for (const testKey of runGroup.test_keys) {
                        this.runGroupsIdsByTestId[testKey] = runGroup.group_key;
                    }
                }

                this.state = {
                    ...this.state,
                    loadState: LoadState.Success,
                    runStatus: RunStatus.Initial,
                    device: device,
                };

                this.initRunGroups();

                if (run) {
                    this.state.runDate = new Date(run.createdAt);
                    // Go ahead and apply the run's state to our tests inside the run groups.
                    // We don't need to recreate any objects since our state is new.
                    const newResultsByTestId: {[testId: string]: ITestAPI} = {};
                    for (const newResult of run.testResults) {
                        newResultsByTestId[newResult.test_id] = newResult;
                    }

                    for (const runGroup of this.state.runGroups) {
                        for (const test of runGroup.tests) {
                            const newResult = newResultsByTestId[test.id];
                            if (newResult) {
                                test.status = newResult.test_status;
                                test.result = newResult.test_detail
                            }
                        }
                    }

                    // Update our device with the stored values for this device.
                    // If we're viewing an old run, these may be different than the current values for the device.
                    this.state.device.location = run.location;
                    this.state.device.serial_number = run.serialNumber;
                    this.state.device.mac_address = run.macAddress;
                    this.selectStartedRunGroups();

                    if (run.status === RunStatus.Running) {
                        // Run is in progress; try and connect to events.
                        const url = this.getSSEURL(this.getSSEStreamName(this.state.device.last_run_id));
                        this.connectToSSE(url)
                            .then(() => {
                                this.selectStartedRunGroups();
                                this.pubSubState.publish();

                            })
                            // We're ignoring connection errors here because our sseStatus will already be updated.
                            // This is just to mask the console error from an unhandled promise rejection.
                            .catch((err) => {});

                        this.state = {
                            ...this.state,
                            runStatus: RunStatus.Running,
                        }
                        this.pubSubState.publish();
                    } else {
                        this.pubSubState.publish();
                    }
                } else {
                    // Device has never been tested, no need to try to connect.
                    this.selectAllRunGroups();
                    this.pubSubState.publish();
                }
            })
            .catch((err) => {
                if (this.isDestroyed) return;
                this.state = {
                    ...this.state,
                    loadState: LoadState.Error,
                    loadError: parseAxiosError(err),
                };
                this.pubSubState.publish();
            });

        // Show loading state
        this.state = {
            ...this.state,
            loadState: LoadState.Loading,
            loadError: '',
        };
        this.pubSubState.publish();
    }

    public startRun(runGroupIds: RunGroupID[]): void {
        if (this.state.runStatus === RunStatus.Starting
            || this.state.runStatus === RunStatus.Running
            || this.state.runStatus === RunStatus.Canceling
        ) {
            console.warn("Can't start run if we're already running.");
            return;
        }

        TestsService.startRun(this.deviceId, runGroupIds).then((response) => {
            if (this.isDestroyed) return;
            this.runId = response.run_id;
            // Success, now connect to the events stream.
            this.connectToSSE(this.getSSEURL(response.sse_stream))

            this.state = {
                ...this.state,
                runDate: new Date(response.device_under_test.created_at),
                runStatus: RunStatus.Running,
            };
            this.pubSubState.publish();
        }).catch((err) => {
            if (this.isDestroyed) return;
            this.state = {
                ...this.state,
                runStatus: RunStatus.Initial,
                runError: parseAxiosError(err),
            };
            this.pubSubState.publish();
        });

        // Reset the run groups, but preserve selection
        const isSelected: {[runGroupId: string]: boolean} = {}
        for (const runGroup of this.state.runGroups) {
            isSelected[runGroup.id] = runGroup.selected;
        }
        this.initRunGroups();
        // Reapply selection
        for (const runGroup of this.state.runGroups) {
            runGroup.selected = isSelected[runGroup.id];
        }

        this.state = {
            ...this.state,
            runGroups: [...this.state.runGroups],
            runStatus: RunStatus.Starting,
            runError: '',
            // It's a new run, so clear the server events log.
            events: [],
        };
        this.pubSubState.publish();
    }

    public stopRun() {
        if (this.state.runStatus === RunStatus.Canceling || this.state.runStatus === RunStatus.Initial) {
            console.warn("Can't stop run if we're already stopping or stopped.");
            return;
        }
        if (!this.runId) {
            console.warn("Can't stop run, no runId. This shouldn't ever happen.");
            return;
        }

        TestsService.stopRun(this.runId).then(() => {
            if (this.isDestroyed) return;
            this.runId = undefined;
            this.disconnectSSE();

            // If we're not connected to events, then any in progress tests will never finish,
            // so manually cancel them.
            this.cancelAnyRunningTests();
            this.state = {
                ...this.state,
                runStatus: RunStatus.Initial,
            }
            this.pubSubState.publish();
        }).catch((err) => {
            if (this.isDestroyed) return;
            // Stopping errored, so we're going back to initial state so that the user can try to start another run.
            // Maybe this run got in an invalid state, like if the run is "inprogress" in the db,
            // but the server got rebooted, so it doesn't know about the run.
            this.state = {
                ...this.state,
                
                runStatus: RunStatus.Initial,
                runError: parseAxiosError(err),
            };
            this.pubSubState.publish();
        });

        this.state = {
            ...this.state,
            runStatus: RunStatus.Canceling,
            runError: '',
        };
        this.pubSubState.publish();
    }

    public destroy = () => {
        this.isDestroyed = true;
        this.disconnectSSE();
    }

    private initRunGroups() {
        // Fill in the tests for each run group. The API only sends us the testIDs for each,
        // but we want to fill in full test objects.
        const runGroups = this.runGroupsAPI.map((runGroupAPI) => {
            return {
                id: runGroupAPI.group_key,
                name: RunGroupsService.getName(runGroupAPI.group_key),
                // Convert testIds to actual tests
                tests: runGroupAPI.test_keys.map((testKey) => {
                    let test: ITest = this.allTestsById[testKey];
                    if (!test) {
                        console.error('initRunGroups - got unknown test key: ', testKey)
                        test = {
                            id: 0,
                            enumKey: testKey,
                            description: 'Unknown test',
                            groupName: 'Unknown',
                            name: 'Unknown test ' + testKey,
                            status: TestStatus.NotStarted,
                        };
                    }
                    // Create a copy so that we don't modify the original
                    return {...test};
                }),
            }
        });
        this.state = {
            ...this.state,
            runGroups: runGroups,
        };
    }

    // This will need to be updated if the server ever changes its method of generating the stream url.
    // This is necessary because the stream url isn't saved in the db, so we don't have a way
    // of getting it if the user refreshes the page.
    private getSSEStreamName(runId: string): string {
        return `${runId}TestResult`;
    }

    private getSSEURL(streamName: string): string {
        return `${process.env.REACT_SERVER_BASE_URL_LOCAL}/events?stream=${encodeURIComponent(streamName)}`;
    }

    // Connects to server sent events.
    private connectToSSE(streamURL: string): Promise<void> {        
        return new Promise((resolve, reject) => {
            // After we connect, we'll wait a short time for the server to have time to catch us up on past events.
            const replayDelay = 1000;

            // Cleanup existing connection
            this.disconnectSSE();

            // Use a context to keep track of when this connection is no longer relevant,
            // like if the user stops the tests and starts a new run.
            const ctx: IContext = {};
            this.sseContext = ctx;
            this.state.sseStatus = SSEStatus.Connecting;

            // We're passing the auth token in a query param because EventSource doesn't support headers.
            const bearerHeader = axiosServer.defaults.headers.common['Authorization'];
            // Remove prefix from beginning.

            const bearer = typeof bearerHeader === "string" && bearerHeader.replace(/^Bearer /, '')
            
            // Create the event connection. The streamURL already has a query param.
            this.sse = new EventSource(`${streamURL}&bearer=${encodeURIComponent(bearer)}`);
            this.sse.onopen = () => {
                if (ctx.isDone) return;

                this.state.sseStatus = SSEStatus.WaitingForReplay;
                // Wait for server to replay all events so that we can ignore control messages.
                setTimeout(() => {
                    if (ctx.isDone) return;

                    // Apply queued events
                    for (const e of this.queuedSSEEvents) {
                        this.applyEventToState(e);
                    }
                    this.queuedSSEEvents = [];

                    this.state = {
                        ...this.state,
                        sseStatus: SSEStatus.Connected,
                    };
                    resolve();
                    this.pubSubState.publish();
                }, replayDelay);
            };
            this.sse.onmessage = (message) => {
                if (ctx.isDone) return;
                this.onSSEMessage(message);
            }
            this.sse.onerror = (err) => {
                if (ctx.isDone) return;
                console.error('SSE Error: ', err);
                ctx.isDone = true;

                // Push an event to tell the user we were disconnected
                const disconnectEvent: ISSEEventError = {
                    event_type: SSEEventType.Error,
                    message: 'Server events connection errored out.',
                };

                this.state = {
                    ...this.state,
                    sseStatus: SSEStatus.Error,
                    isWaitingOnReboot: false,
                    events: [...this.state.events, disconnectEvent]
                }
                reject();
                this.pubSubState.publish();
            }
        });
    }

    private disconnectSSE() {
        if (this.sse) {
            this.sse.close();
            this.sse = undefined;
            // Let the user know we're no longer listening for events
            this.state.events = [...this.state.events, {
                event_type: SSEEventType.Info,
                message: 'Disconnected from events.',
            }];
        }
        if (this.sseContext) {
            this.sseContext.isDone = true;
            this.sseContext = undefined;
        }
        this.queuedSSEEvents = [];
        this.state = {
            ...this.state,
            sseStatus: SSEStatus.Initial,
            isWaitingOnReboot: false,
        };
    }

    private onSSEMessage = (message: MessageEvent<string>) => {
        let event: ISSEEvent;
        try {
            event = JSON.parse(message.data)
        } catch(err) {
            console.error("Error parsing sse message json. Err, message: ", err, message);
            return;
        }

        if (this.state.sseStatus === SSEStatus.WaitingForReplay) {
            this.queuedSSEEvents.push(event);
        } else {
            this.applyEventToState(event);
            this.pubSubState.publish();
        }
    }

    // Applies a server event to our state. Does not publish state updates to avoid lots of rerenders
    // while applying queued state. Instead, callers should publish a state update after calling this method.
    private applyEventToState = (e: ISSEEvent) => {
        let eventsToAppend: ISSEEvent[] = [e];

        switch (e.event_type) {
            case SSEEventType.NeedReboot:
                this.state = {
                    ...this.state,
                    isWaitingOnReboot: true,
                };
                this.pubSubState.publish();
                break;
            case SSEEventType.GotReboot:
                this.state = {
                    ...this.state,
                    isWaitingOnReboot: false,
                }
                this.pubSubState.publish();
                break;
            case SSEEventType.RunStop:
                this.state = {
                    ...this.state,
                    runStatus: RunStatus.Initial,
                }
                const ctx = this.sseContext
                // Give the server a short time to send any last events before we disconnect
                setTimeout(() => {
                    if (!ctx.isDone) {
                        this.disconnectSSE();
                        this.state = {...this.state};
                        this.pubSubState.publish();
                    }
                }, 1000);
                break;
            case SSEEventType.Heartbeat:
                eventsToAppend = [];
                break;
            case SSEEventType.TestsState: {
                // Apply each test
                for (const newTestState of e.tests) {
                    let foundTest = false;

                    // Update our test's state. We need to find the rungroup containing this test.
                    const runGroupId = this.runGroupsIdsByTestId[newTestState.test_id];

                    for (let i = 0; i < this.state.runGroups.length && !foundTest; i++) {
                        const runGroup = this.state.runGroups[i];

                        if (runGroup.id === runGroupId) {
                            // Found a match, now find the test within this group.
                            for (let testsI = 0; testsI < runGroup.tests.length && !foundTest; testsI++) {
                                const test = runGroup.tests[testsI];

                                if (test.id === newTestState.test_id || test.enumKey === newTestState.test_id) {
                                    foundTest = true;

                                    // Update our state. Unfortunately we need to create new objects up to the top level
                                    // so that react recognizes the change.
                                    runGroup.tests[testsI] = {
                                        ...test,
                                        status: TestStatusGetID(newTestState.status),
                                        result: newTestState.result,
                                    };
                                    runGroup.tests = [...runGroup.tests];
                                    this.state.runGroups[i] = {...runGroup};
                                    // We'll recreate the top-level state object outside the loop.
                                }
                            }
                        }
                    }
                }

                // We're going to create a new event for each test in the original event to make it simpler to display them.
                eventsToAppend = [];
                for (const newTestState of e.tests) {
                    const singleTest: ISSEEventTestState = {
                        event_type: SSEEventType.TestState,
                        test_id: typeof newTestState.test_id === "string" ? this.allTestsById[newTestState.test_id]?.id : newTestState.test_id,
                        status: TestStatusGetID(newTestState.status),
                        result: newTestState.result,
                    }
                    eventsToAppend.push(singleTest);
                }

                // Update top-level object so that the runGroup updates get recognized
                this.state = {
                    ...this.state,
                    runGroups: [...this.state.runGroups],
                }
                break;
            }
        }
        
        this.state = {
            ...this.state,
            events: this.state.events.concat(eventsToAppend),
        }
    }

    private selectAllRunGroups() {
        for (let i = 0; i < this.state.runGroups.length; i++) {
            const runGroup = this.state.runGroups[i];
            this.state.runGroups[i] = {
                ...runGroup,
                selected: true,
            };
        }
        this.state.runGroups = [...this.state.runGroups];
    }

    private selectStartedRunGroups() {
        for (let i = 0; i < this.state.runGroups.length; i++) {
            const runGroup = this.state.runGroups[i];
            const hasInProgressTests = runGroup.tests.some((test) => {
                return test.status !== TestStatus.NotStarted;
            });
            // Select the group if it has tests that have been run.
            if (hasInProgressTests) {
                this.state.runGroups[i] = {
                    ...runGroup,
                    selected: true,
                };
            }
        }
        this.state = {
            ...this.state,
            runGroups: [...this.state.runGroups],
        }
    }

    private cancelAnyRunningTests() {
        for (let i = 0; i < this.state.runGroups.length; i++) {
            const runGroup = this.state.runGroups[i];
            let didUpdateTest = false

            for (let testsI = 0; testsI < runGroup.tests.length; testsI++) {
                const test = runGroup.tests[testsI];
                if (test.status === TestStatus.InProgress || test.status === TestStatus.Queued) {
                    runGroup.tests[testsI] = {
                        ...test,
                        status: TestStatus.Canceled,
                        result: t('tests.statusCanceled'),
                    }
                    didUpdateTest = true;
                }
            }

            // If we updated a test, we need to recreate the group so the change is detected
            if (didUpdateTest) {
                runGroup.tests = [...runGroup.tests];
                this.state.runGroups[i] = {
                    ...runGroup,
                }
            }
        }
        this.state = {
            ...this.state,
            runGroups: [...this.state.runGroups],
        }
    }

    // ----------------------- Hooks -----------------------
    // These allow functional components to react to changes in our data.

    public useState = (): [ITestsModelState, (newState: ITestsModelState) => void] => {
        const [state, setStateInternal] = useState(this.state);
        // Every render, subscribe to the pubsub. Return the unsubscribe function so it unsubscribes before each rerender,
        useEffect(() => {
            return this.pubSubState.subscribe(() => {
                // On publish, call setState to trigger a render for any components using this hook.
                setStateInternal(this.state);
            })
        })

        // Register for cleanup. This will be called when the component is unmounted.
        // NOTE: This assumes that this model will only ever be hooked into by 1 component, which is currently the case.
        // If we ever need to keep this model around during multiple components' lifecycles, we'll need to do 
        // something more sophisticated.
        useEffect(() => {
            return this.destroy;
        }, [])

        // We're exposing setState so that the tests page can modify the run groups to make them selected.
        const setState = (newState: ITestsModelState) => {
            this.state = newState;
            setStateInternal(newState);
        };
        return [state, setState];
    }
}

export const TestsModelFactory = new _TestsModelFactory();
